import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigationEffect } from 'src/redux/slices/navigation/hooks'
import { downloadScenarioModel, updateScenario } from 'src/redux/slices/project/endpoints'
import { selectActiveProjectId } from 'src/redux/slices/project/selectors'
import { uploadBaseUpgradingFile } from 'src/redux/slices/upgrades/endpoints'
import { selectTemplates } from 'src/redux/slices/upgrades/selectors'
import { Scenario } from 'src/types/database-model'
import { TextFieldOnChangeEvent } from 'src/types/event-handlers'

import { Button, Grid, TextField, Typography } from '@material-ui/core'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'

import DeleteScenario from './DeleteScenario'
import DropzoneResults from './DropzoneResults'
import EditScenario from './EditScenario'

///// Types
type ScenarioOverviewRowProps = {
  scenario: NonNullable<Scenario>
}

///// Component
const ScenarioOverviewRow: React.FC<ScenarioOverviewRowProps> = ({ scenario }) => {
  const templates = useSelector(selectTemplates)
  const project_id = useSelector(selectActiveProjectId)
  const [trigger, { data, isLoading }] = uploadBaseUpgradingFile.useMutation({
    fixedCacheKey: 'loadUpgradeScenario',
  })

  const [triggerDownloadScenarioModel, { isLoading: isDownloadingScenarioModel }] =
    downloadScenarioModel.useMutation()

  const goToUpgrader = useCallback(() => {
    trigger({
      body: { id: scenario.id, from_cloud: true },
    })
  }, [trigger, scenario.id])

  const downloadModelFile = useCallback(async () => {
    triggerDownloadScenarioModel({
      source_umi_file_location: scenario.source_umi_file_location,
      source_umi_file_version: scenario.source_umi_file_version,
      title: scenario.title,
    })
  }, [scenario])

  useNavigationEffect({
    destination: '../upgrades/assign',
    condition: templates,
  })

  const [isEditingScenarioMeta, setIsEditingScenarioMeta] = useState(false)

  const toggleEditingScenarioMeta = useCallback(
    () => setIsEditingScenarioMeta((prev) => !prev),
    [setIsEditingScenarioMeta],
  )

  const [scenarioMeta, setScenarioMeta] = useState({
    title: scenario.title,
    description: scenario.description,
  })

  const changeTitle = useCallback(
    (e: TextFieldOnChangeEvent) => {
      setScenarioMeta((prev) => ({ ...prev, title: e.target.value }))
    },
    [setScenarioMeta],
  )
  const changeDesc = useCallback(
    (e: TextFieldOnChangeEvent) => {
      setScenarioMeta((prev) => ({ ...prev, description: e.target.value }))
    },
    [setScenarioMeta],
  )

  const [updateScenarioMeta, { isLoading: isUpdatingScenarioMeta }] = updateScenario.useMutation()
  const submit = useCallback(() => {
    const { title, description } = scenarioMeta
    updateScenarioMeta({ id: scenario.id, update: { title, description } })
    toggleEditingScenarioMeta()
  }, [scenarioMeta, scenario, updateScenarioMeta, toggleEditingScenarioMeta])

  return (
    <Grid container item xs={12} spacing={1} alignItems='center' justifyContent='flex-start'>
      <Grid item>
        <DeleteScenario id={scenario.id} disabled={isEditingScenarioMeta} />
      </Grid>
      <Grid item>
        <EditScenario
          toggle={toggleEditingScenarioMeta}
          isEditing={isEditingScenarioMeta}
          submit={submit}
        />
      </Grid>
      <Grid item xs={isEditingScenarioMeta ? 2 : 1}>
        {!isEditingScenarioMeta && (
          <Typography>{isUpdatingScenarioMeta ? scenarioMeta.title : scenario.title}</Typography>
        )}
        {isEditingScenarioMeta && (
          <TextField
            label='Scenario Title'
            defaultValue={scenario.title}
            size='small'
            fullWidth
            onChange={changeTitle}
          />
        )}
      </Grid>
      <Grid item xs={isEditingScenarioMeta ? 3 : 2}>
        {!isEditingScenarioMeta && (
          <Typography variant='caption'>
            {`${(isUpdatingScenarioMeta ? scenarioMeta.description : scenario.description)?.substr(
              0,
              26,
            )}${
              ((isUpdatingScenarioMeta ? scenarioMeta.description : scenario.description)?.length ??
                0) > 30
                ? '...'
                : ''
            }`}
          </Typography>
        )}
        {isEditingScenarioMeta && (
          <TextField
            label='Scenario Description'
            defaultValue={scenario.description}
            size='small'
            fullWidth
            onChange={changeDesc}
          />
        )}
      </Grid>
      <Grid item>
        <Button
          variant='outlined'
          size='small'
          color='primary'
          onClick={goToUpgrader}
          disabled={isLoading}
        >
          Define Upgrades
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant='outlined'
          size='small'
          color='primary'
          endIcon={<CloudDownloadIcon />}
          onClick={downloadModelFile}
          disabled={!scenario.source_umi_file_location || isDownloadingScenarioModel}
        >
          Model File
        </Button>
      </Grid>
      <Grid item>
        <DropzoneResults scenario={scenario} />
      </Grid>
    </Grid>
  )
}

export default ScenarioOverviewRow
